// Generic single-column label-left/value-right list of rows. Exists
// alongside `FieldGrid` (not a replacement for it) because `FieldGrid` is
// hard-wired to a 2-column grid — inside a narrow pane (e.g. the Feedback
// Inbox right rail's 340px width, `RAIL_WIDTH_PX`) a 2-column grid squishes
// each column too narrow to read, whereas single-column label/value rows
// (like the Feedback Inbox rail spec, VISUAL-SPEC.md §4) stay legible. Mirrors
// the dt/dd + UA-margin-reset row pattern in `OrderSnapshotReceipt.tsx`.
import { BASE_FONT_FAMILY } from '@thanx/uikit/theme/typography'
import palette from 'constants/palette'
import React from 'react'
import { useStyletron } from 'styletron-react'

// Row type matches the rail prototype's `.fb-kv` 1:1 (AI-1424,
// rai-landingpage/option-2 v64): 13px/1.4 regular, sentence-case label,
// grey90 value, grey10 rule under every row. uikit's SmallCaps and
// BodyText variants carry their own case, tracking and size, so the row
// sets its type directly.
const ROW_FONT_SIZE = '13px'
const ROW_LINE_HEIGHT = 1.4
const ROW_PADDING = '7px 0'

export type KeyValueListItem = {
  label: string
  value: React.ReactNode
  key?: string
}

type Props = {
  items: KeyValueListItem[]
  // Required: the list's accessible name. Every consumer already has a
  // translated section heading to hand, and keeping it required means this
  // shared component owns no locale keys of its own.
  ariaLabel: string
}

const KeyValueList = ({ items, ariaLabel }: Props) => {
  const [css] = useStyletron()

  const rowClass = css({
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    padding: ROW_PADDING,
    borderBottom: `1px solid ${palette.grey10}`,
    fontFamily: BASE_FONT_FAMILY,
    fontSize: ROW_FONT_SIZE,
    fontWeight: 400,
    lineHeight: ROW_LINE_HEIGHT,
  })
  // dt/dd carry default browser margins (dd indents ~40px) — reset so the
  // flex space-between row controls layout, not the UA stylesheet. Bootstrap
  // also bolds `dt` and gives both a 1.43 line-height, so each cell restates
  // the row's weight and line-height.
  const termClass = css({
    margin: 0,
    fontWeight: 400,
    lineHeight: ROW_LINE_HEIGHT,
    // The prototype uses grey50 (2.27:1 on white); grey70 passes AA.
    color: palette.grey70,
    textTransform: 'none',
  })
  const valueClass = css({
    margin: 0,
    fontWeight: 400,
    lineHeight: ROW_LINE_HEIGHT,
    minWidth: 0,
    wordBreak: 'break-word',
    color: palette.grey90,
    textAlign: 'right',
  })

  return (
    <dl className={css({ margin: 0 })} aria-label={ariaLabel}>
      {items.map((item, index) => (
        // Composite key (index + label) matches FieldGrid's own idiom: labels
        // arrive already-translated, and two could collide in some locale —
        // React would silently dedup and drop a row otherwise.
        <div className={rowClass} key={item.key ?? `${index}-${item.label}`}>
          <dt className={termClass}>{item.label}</dt>
          <dd className={valueClass}>{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

export default KeyValueList
